import React, { ReactElement, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'immutable';

// Components
import TagsSearchBar from './components/TagsSearchBar';
import TagsCreateButtons from './components/TagsCreateButtons';
import TagLists from './components/TagLists';

// Utils
import { TagsToolbarPopupProps } from './popup/TagsToolbarPopup';
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import useTagHandlers from './hooks/useTagHandlers';

// Selectors
import { getElement } from '../../../../../element/selectors/elementSelector';
import { getSelectedTaskIdSelector } from '../../../selector/selectionToolsSelector';

// Types
import { UseTagPopupsStateReturn } from './hooks/useTagPopupsState';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

import './TagsToolContent.scss';

export type TagsToolContentProps = TagsToolbarPopupProps & UseTagPopupsStateReturn;

const TagsToolContent = (props: TagsToolContentProps): ReactElement => {
    const { handleClickEdit, shareRootPath, selectedElements, allTags, userRootPath, retreivingElementsForTag } = props;

    const [searchString, setSearchString] = useState<string>('');

    const elementIds = selectedElements.map(getElementId).toArray();
    const selectedTaskListId = useSelector(getSelectedTaskIdSelector);
    const focusElementIds = selectedTaskListId ? [selectedTaskListId] : elementIds;
    const elements: List<ImMNElement> = useSelector((state) => {
        return List(focusElementIds.map((elementId) => getElement(state, { elementId })));
    });

    const { handleToggleTagSelection, handleCreateTag, handleCreateCopyAndAddToElement } = useTagHandlers(
        props,
        searchString,
        setSearchString,
        elements,
    );

    const showCreateButtons = useMemo(
        () => searchString && !allTags.some((tag) => tag.name === searchString),
        [searchString, allTags],
    );
    return (
        <div className="TagsToolContent">
            <TagsSearchBar value={searchString} onChange={setSearchString} handleCreateTag={handleCreateTag} />
            <TagLists
                handleToggleTagSelection={handleToggleTagSelection}
                handleClickEdit={handleClickEdit}
                handleCreateCopyAndAddToElement={handleCreateCopyAndAddToElement}
                elements={elements}
                searchString={searchString}
                allTags={allTags}
                userRootPath={userRootPath}
                retreivingElementsForTag={retreivingElementsForTag}
            />
            {showCreateButtons && <TagsCreateButtons handleCreateTag={handleCreateTag} shareRootPath={shareRootPath} />}
        </div>
    );
};

export default TagsToolContent;
